import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgNotFoundTemplateDirective
} from '@ng-select/ng-select';
import { Office } from 'app/core/models/office.model';
import { OfficeFilters } from 'app/core/models/user-settings/office-search-settings.model';
import { OfficeService, PagedOffices } from 'app/core/services/office.service';
import { PhoneNumberPipe } from '../../pipes/phone-number.pipe';

// searches offices by fax number, matching the search used when associating offices to providers
@Component({
  selector: 'app-office-fax-select',
  templateUrl: './office-fax-select.component.html',
  styleUrls: ['./office-fax-select.component.scss'],
  imports: [
    FormsModule,
    AsyncPipe,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgNotFoundTemplateDirective,
    PhoneNumberPipe
  ]
})
export class OfficeFaxSelectComponent implements OnInit {
  private officeService = inject(OfficeService);

  @Input() placeholder = 'Search for Office by Fax';
  @Input() labelForId: string = null;
  @Input() office: Office = null;
  @Output() officeChange = new EventEmitter<Office>();

  readonly minFaxLength = 3;

  offices$: Observable<Office[]>;
  searchInput$ = new Subject<string>();
  loading = false;

  ngOnInit() {
    this.offices$ = this.searchInput$.pipe(
      distinctUntilChanged(),
      tap(() => this.loading = true),
      debounceTime(500),
      switchMap(term =>
        this.searchOffices(term).pipe(
          catchError(() => of(new PagedOffices())),
          tap(() => this.loading = false),
          map(result => result.records)
        ))
    );
  }

  onChange(office: Office) {
    this.office = office || null;
    this.officeChange.emit(this.office);
  }

  private searchOffices(term: string): Observable<PagedOffices> {
    const fax = (term || '').replace(/[^0-9]/g, '');

    if (fax.length < this.minFaxLength) {
      return of(new PagedOffices());
    }

    const filters = new OfficeFilters();
    filters.fax = fax;

    return this.officeService.getList({ page: 1, pageSize: 20 }, filters);
  }
}
